import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlayIcon, PauseIcon } from '@utils/icons';
import { formatElapsed } from '../../utils/formatElapsed';
import styles from './VoicePlayer.module.css';

// Only one voice note plays at a time. The note that is playing leaves its
// pause function here; the next note to start calls it, then takes its place.
let pauseThePlayingNote: (() => void) | null = null;

/** Called when a note starts. Returns what to call when it stops. */
function startPlayingAlone(pause: () => void): () => void {
  if (pauseThePlayingNote && pauseThePlayingNote !== pause) pauseThePlayingNote();
  pauseThePlayingNote = pause;
  return () => {
    if (pauseThePlayingNote === pause) pauseThePlayingNote = null;
  };
}

/** The scrubber's knob diameter; must match `.scrubber` thumb size in the CSS. */
const KNOB_PX = 12;

interface IVoicePlayerProps {
  /** Unset while a local note's bytes are still being finalised. */
  url?: string;
  durationMs: number;
  className?: string;
}

// Play/pause, the elapsed time, a scrubber, the total — over a hidden <audio>,
// so the browser's own controls (and their volume slider) never show. The
// composer's preview and a received note share it.
export function VoicePlayer({ url, durationMs, className }: IVoicePlayerProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const stoppedRef = useRef<(() => void) | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);

  // A MediaRecorder blob often reports its duration as Infinity until played,
  // so the recorded duration is the reliable denominator for the scrubber.
  const totalMs = durationMs > 0 ? durationMs : currentMs;
  const progress = totalMs > 0 ? Math.min(currentMs / totalMs, 1) : 0;
  // The fill ends under the knob's centre. A range input keeps its knob inside
  // the track, so the centre travels from half a knob in to half a knob short
  // of the end — a plain percentage of the width only meets it at the middle.
  const fillEnd = `calc(${progress * 100}% + ${KNOB_PX / 2 - progress * KNOB_PX}px)`;

  function stopped() {
    stoppedRef.current?.();
    stoppedRef.current = null;
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentMs(audio.currentTime * 1000);
    const onEnd = () => {
      setPlaying(false);
      setCurrentMs(0);
      stopped();
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
      stopped();
    };
  }, []);

  function pause() {
    audioRef.current?.pause();
    setPlaying(false);
  }

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      pause();
      stopped();
    } else {
      stoppedRef.current = startPlayingAlone(pause);
      setPlaying(true);
      // A note the browser can't decode rejects; show it as stopped again.
      audio.play().catch(() => {
        setPlaying(false);
        stopped();
      });
    }
  }

  function seek(ms: number) {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = ms / 1000;
    setCurrentMs(ms);
  }

  return (
    <div className={`${styles.player} ${className ?? ''}`}>
      <button
        type="button"
        className={styles.playBtn}
        onClick={toggle}
        aria-label={playing ? t('comms.attachment.pause') : t('comms.attachment.play')}
      >
        {playing ? <PauseIcon size={14} /> : <PlayIcon size={14} />}
      </button>
      <span className={styles.elapsed}>{formatElapsed(currentMs)}</span>
      <input
        type="range"
        className={styles.scrubber}
        min={0}
        max={totalMs}
        step={100}
        value={currentMs}
        onChange={(e) => seek(Number(e.currentTarget.value))}
        style={{ '--progress': fillEnd } as React.CSSProperties}
        aria-label={t('comms.attachment.voice')}
      />
      <span className={styles.total}>{formatElapsed(totalMs)}</span>
      <audio ref={audioRef} src={url} preload="metadata" />
    </div>
  );
}
